import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setPowerPlant } from "../../../../store/features/shipBuilderSlice";
import { POWER_PLANTS } from "../../../../constants/powerPlants";
import { PowerPlant, PowerPlantType } from "../../../../types/powerPlants";
import { formatCurrency, formatNumber } from "../../../../utils/formatting";
// import { calculatePowerRequirements } from "../../../../utils/calculations/powerCalculations"; // No longer used - using centralized calculations
import { selectCentralizedPowerRequirements, selectPowerCalculationDebug } from "../../../../utils/calculations/centralizedPowerCalculations";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { PowerPlantSummary } from "./PowerPlantSummary";

/**
 * Component for selecting and configuring ship power plants
 * Implements Traveller power requirements system:
 * - Basic ship systems: 20% of hull tonnage
 * - Maneuver drive: 10% of hull tonnage × thrust rating
 * - Jump drive: 10% of hull tonnage × jump rating (fusion/antimatter only)
 *
 * @component
 * @example
 * ```tsx
 * <PowerPlantSelector />
 * ```
 */
export const PowerPlantSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);

  const shipSize = useAppSelector(selectors.selectShipSize);

  // All power calculations are now handled by centralized selectors

  // State for manual quantity adjustment
  const [manualQuantity, setManualQuantity] = React.useState<number | null>(
    null,
  );
  const [useManualQuantity, setUseManualQuantity] = React.useState(false);

  // Use centralized power calculation
  const powerRequirements = useAppSelector(selectCentralizedPowerRequirements);
  const powerDebug = useAppSelector(selectPowerCalculationDebug);

  // Debug logging to identify power calculation issues
  console.log('PowerPlantSelector Debug (Centralized):', {
    shipSize,
    drives: drives.map(d => ({ type: d.type, rating: d.rating })),
    powerRequirements,
    debug: powerDebug,
  });

  const selectedType: PowerPlantType = powerPlant?.type || "fission";
  const selectedSpec = POWER_PLANTS[selectedType];

  // Calculate power plant size needed
  // Per Traveller rules: power plant should handle basic systems + maneuver drive + jump drive
  const autoPowerPlantTons = Math.round(
    Math.max(0.1, 
      powerRequirements.normalOperations / selectedSpec.powerPerTon
    ) * 100
  ) / 100; // Round to nearest 0.01
  const effectivePowerPlantTons =
    useManualQuantity && manualQuantity !== null
      ? manualQuantity
      : autoPowerPlantTons;
  const powerPlantCost = effectivePowerPlantTons * selectedSpec.costPerTon;
  const powerPlantPower = effectivePowerPlantTons * selectedSpec.powerPerTon;
  const maintenanceCost =
    effectivePowerPlantTons * selectedSpec.maintenanceCost;

  // Update manual quantity when auto calculation changes
  React.useEffect(() => {
    if (!useManualQuantity) {
      setManualQuantity(autoPowerPlantTons);
    }
  }, [autoPowerPlantTons, useManualQuantity]);

  // Sync Redux state when power plant size changes due to power requirement changes
  React.useEffect(() => {
    if (powerPlant && !useManualQuantity) {
      // Only update if the calculated size is different from what's stored in Redux
      if (effectivePowerPlantTons !== powerPlant.weight) {
        const updatedPowerPlant: PowerPlant = {
          ...powerPlant,
          weight: effectivePowerPlantTons,
          power: powerPlantPower,
          cost: powerPlantCost * 1000000,
          maintenanceCost: maintenanceCost * 1000000,
        };
        dispatch(setPowerPlant(updatedPowerPlant));
      }
    }
  }, [effectivePowerPlantTons, powerPlantPower, powerPlantCost, maintenanceCost, useManualQuantity, dispatch]);

  const handleTypeChange = (type: PowerPlantType) => {
    const plantSpec = POWER_PLANTS[type];
    if (!plantSpec) return;

    const updatedPowerPlant: PowerPlant = {
      type: plantSpec.type,
      rating: 1,
      cost: powerPlantCost * 1000000,
      weight: effectivePowerPlantTons,
      power: powerPlantPower,
      techLevel: plantSpec.techLevel,
      maintenanceCost: maintenanceCost * 1000000,
      description: plantSpec.description,
    };

    dispatch(setPowerPlant(updatedPowerPlant));
  };

  // Auto-select the best available power plant when tech level changes or power plant is first created
  React.useEffect(() => {
    // Find the highest-rated power plant available for current tech level
    const availablePowerPlants = Object.entries(POWER_PLANTS)
      .filter(([_, plantSpec]) => tlOverrideActive || plantSpec.techLevel <= techLevel)
      .sort(([_, a], [__, b]) => b.techLevel - a.techLevel); // Sort by tech level descending

    if (availablePowerPlants.length > 0) {
      const [bestPowerPlantType] = availablePowerPlants[0];
      const bestPowerPlantSpec = POWER_PLANTS[bestPowerPlantType as keyof typeof POWER_PLANTS];
      
      // Auto-select if:
      // 1. No power plant is configured at all, OR
      // 2. Current power plant is invalid at new tech level (TL too high for ship), OR
      // 3. There's a better power plant available (any TL improvement)
      // This allows auto-selection whenever a better power plant becomes available
      const shouldAutoSelect = 
        !powerPlant || !powerPlant.type || // No power plant selected
        (powerPlant.type && POWER_PLANTS[powerPlant.type as keyof typeof POWER_PLANTS]?.techLevel > techLevel) || // Current power plant invalid for tech level
        (powerPlant.type && POWER_PLANTS[powerPlant.type as keyof typeof POWER_PLANTS]?.techLevel < bestPowerPlantSpec.techLevel); // Better power plant available

      if (shouldAutoSelect) {
        handleTypeChange(bestPowerPlantType as PowerPlantType);
      }
    }
  }, [techLevel, powerPlant, tlOverrideActive]);

  const handleQuantityChange = (quantity: number) => {
    setManualQuantity(quantity);
    const updatedPowerPlant: PowerPlant = {
      type: selectedType,
      rating: 1,
      cost: quantity * selectedSpec.costPerTon * 1000000,
      weight: quantity,
      power: quantity * selectedSpec.powerPerTon,
      techLevel: selectedSpec.techLevel,
      maintenanceCost: quantity * selectedSpec.maintenanceCost * 1000000,
      description: selectedSpec.description,
    };

    dispatch(setPowerPlant(updatedPowerPlant));
  };

  const handleManualToggle = (useManual: boolean) => {
    setUseManualQuantity(useManual);
    if (!useManual) {
      setManualQuantity(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Power Plant Configuration
        </h2>
      </div>

      {/* Standard Power Plant Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Power Plant Selection</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Plant Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value as PowerPlantType)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              {Object.entries(POWER_PLANTS)
                .filter(([_, plant]) => tlOverrideActive || plant.techLevel <= techLevel)
                .map(([key, plant]) => {
                  // Add warning note for power plants that cannot support Jump Drives
                  let displayName = plant.name.includes('(TL') ? plant.name : `${plant.name} (TL ${plant.techLevel})`;
                  if (key === 'fission' || key === 'chemical') {
                    displayName += ' - Cannot power Jump Drives';
                  }
                  return (
                    <option key={key} value={key}>
                      {displayName}
                    </option>
                  );
                })}
            </select>
            
            {/* Warning message for power plants that cannot support Jump Drives */}
            {(selectedType === 'fission' || selectedType === 'chemical') && (
              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Jump Drive Limitation
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      {selectedType === 'fission' ? 'Fission' : 'Chemical'} Power Plants cannot provide sufficient power for Jump Drives. 
                      Only Fusion and Antimatter Power Plants can support Jump Drive operations.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Plant Size (tons)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={effectivePowerPlantTons}
                onChange={(e) => handleQuantityChange(parseFloat(e.target.value) || 1)}
                className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useManualQuantity}
                  onChange={(e) => handleManualToggle(e.target.checked)}
                  className="mr-2 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">Manual</span>
              </label>
            </div>
            {!useManualQuantity && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auto-calculated based on power requirements
              </p>
            )}
          </div>
        </div>

        {/* Power Requirements Display */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-6">
          <h4 className="font-medium mb-3 text-gray-900 dark:text-gray-100">Power Requirements</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-medium">Normal Operations:</span>
              <span className="ml-2">{formatNumber(powerRequirements.normalOperations)} Power</span>
            </div>
            <div>
              <span className="font-medium">Jump Operations:</span>
              <span className="ml-2">{formatNumber(powerRequirements.jumpOperations)} Power</span>
            </div>
            <div>
              <span className="font-medium">Maximum Load:</span>
              <span className="ml-2">{formatNumber(powerRequirements.maximumLoad)} Power</span>
            </div>
          </div>
        </div>

        {/* Power Plant Details */}
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
          <h4 className="font-medium mb-3 text-gray-900 dark:text-gray-100">Selected Power Plant Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-medium">Power Output:</span>
              <span className="ml-2">{formatNumber(powerPlantPower)} Power</span>
            </div>
            <div>
              <span className="font-medium">Cost:</span>
              <span className="ml-2">{formatCurrency(powerPlantCost * 1000000)}</span>
            </div>
            <div>
              <span className="font-medium">Maintenance:</span>
              <span className="ml-2">{formatCurrency(maintenanceCost * 1000000)}/month</span>
            </div>
            <div>
              <span className="font-medium">Tech Level:</span>
              <span className="ml-2">{selectedSpec.techLevel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Power Plant Summary */}
      <PowerPlantSummary />


    </div>
  );
};
